import type { PromptListTopBrand } from '../types';
import { findBrandForDomainFilter } from './domain-filter-utils';
import type { CompetitorInsight, DomainFilterValue } from '../types';
import { sortPromptListBrands } from './prompt-top-brands';
import { averageAiMentions, resolveAiMentions } from './visibility-metrics';

export { findBrandForDomainFilter } from './domain-filter-utils';

export interface PromptDomainMetrics {
  visibility: number | null;
  avgPosition: number | null;
  aiMentions: number | null;
}

export function getPromptBrandRows(prompt: {
  brands?: PromptListTopBrand[];
  topBrands?: PromptListTopBrand[];
}): PromptListTopBrand[] {
  if (prompt.brands?.length) return prompt.brands;
  return prompt.topBrands ?? [];
}

function roundPosition(value: number): number {
  return Math.round(value * 10) / 10;
}

function averagePosition(brands: PromptListTopBrand[]): number | null {
  const positions = brands
    .map((brand) => brand.avgPosition)
    .filter((position): position is number => position != null && position > 0);
  if (!positions.length) return null;
  return roundPosition(positions.reduce((sum, position) => sum + position, 0) / positions.length);
}

/** Matches backend computePromptVisibilityScore — average of top 5 brands by visibility. */
export function computePromptVisibilityScore(brands: PromptListTopBrand[]): number | null {
  if (!brands.length) return null;
  const top = sortPromptListBrands(brands).slice(0, 5);
  if (!top.length) return null;
  return Math.round(top.reduce((sum, brand) => sum + brand.visibilityScore, 0) / top.length);
}

export function computePromptDomainMetrics(
  brands: PromptListTopBrand[],
  filter: DomainFilterValue,
  competitors: CompetitorInsight[],
): PromptDomainMetrics {
  if (!brands.length) {
    return { visibility: null, avgPosition: null, aiMentions: null };
  }

  if (filter === 'all') {
    return {
      visibility: computePromptVisibilityScore(brands),
      avgPosition: averagePosition(brands),
      aiMentions: averageAiMentions(brands),
    };
  }

  const brand = findBrandForDomainFilter(brands, filter, competitors);
  if (!brand) {
    return { visibility: null, avgPosition: null, aiMentions: null };
  }

  return {
    visibility: brand.visibilityScore > 0 ? brand.visibilityScore : null,
    avgPosition: brand.avgPosition,
    aiMentions: resolveAiMentions(brand),
  };
}
